import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import {
  Eviction,
  IntakeForm,
  IntakeFormSection,
  IntakeOption,
  Organization,
  Tenant,
  User,
  Workflow,
  WorkflowStep
} from '@ee/common/models';
import { ClientService, IntakeFormService, UserService } from '@ee/common/services';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  GenerateEvictionClaimsForm,
  GenerateEvictionDocumentForm,
  GenerateExistingAttachmentsForm,
  GenerateEvictionLeaseTermsForm,
  GenerateEvictionPropertyForm,
  GenerateEvictionTenantForm,
  GenerateInternalFieldsForm, GenerateOtherFieldsForm,
  GenerateSplitEvictionForm,
  WorkflowDetailsForm
} from '@ee/common/forms';
import { AccountType, IntakeFormFields, IntakeFormSections, IntakeFormType } from '@ee/common/enums';

@Injectable({ providedIn: 'root' })
export class IntakeEvictionFormService implements OnDestroy {
  private subs: Subscription[] = [];
  public splitForm: UntypedFormGroup;
  public intakeForm: IntakeForm | undefined;
  public selectedWorkflow: Workflow | undefined = undefined;
  public workflows: Workflow[] = [];
  public workflows$: Subject<Workflow[]> = new Subject<Workflow[]>();
  public plaintiffHistory: string[] = [];
  public defaultWorkflowId: string | undefined;
  public loggedInOrgId: string | undefined;
  public loggedInOrgType: AccountType | undefined;
  public selectedClient: Organization | undefined;
  public selectedClient$: Subject<Organization> = new Subject<Organization>();
  public attorneyId: string | undefined;
  public isPublicIntakeForm: boolean = false;
  public isInitialized = false;
  public isInitialized$ = new Subject<boolean>();
  public selectedAttorneyId$ = new Subject<string>();
  public evictionDetails: Eviction | undefined = undefined;
  public userSuggestions: User[] = [];
  /** Property-derived plaintiff options (PM property-linked mode) — see [setPropertyPlaintiffs]. */
  private propertyPlaintiffs: string[] = [];
  private propertyPrimaryPlaintiff: string | null = null;

  constructor(
    private fb: UntypedFormBuilder,
    private intakeFormService: IntakeFormService,
    private clientService: ClientService,
    private userService: UserService
  ) {}

  async initialize(
    isPublicIntakeForm: boolean,
    attorneyId: string,
    loggedInUser: User | null,
    loggedInOrg: Organization | null,
    cloneCase: Eviction | null = null
  ) {
    if (this.isInitialized) {
      return;
    }
    this.loggedInOrgType = loggedInOrg?.type;
    this.loggedInOrgId = loggedInOrg?.id;
    this.attorneyId = attorneyId;
    this.isPublicIntakeForm = isPublicIntakeForm;
    this.defaultWorkflowId = loggedInOrg?.default_workflow_id;
    this.splitForm = GenerateSplitEvictionForm(this.fb, this.loggedInOrgType, isPublicIntakeForm);
    const workflowPatch: any = { attorney_id: attorneyId };

    if (!isPublicIntakeForm && this.loggedInOrgType === AccountType.CLIENT) {
      workflowPatch.user_details = loggedInUser;
      workflowPatch.client_details = loggedInOrg;
      this.selectedClient = loggedInOrg;
      this.defaultWorkflowId = undefined;
    }

    this.workflowDetails.patchValue(workflowPatch);

    if (!isPublicIntakeForm && this.loggedInOrgType === AccountType.CLIENT) {
      // let's treat a client that was invited by an attorney as exclusive to attorney unless they're a pro user
      await this.setupForms(cloneCase);
    }

    if (this.loggedInOrgType === AccountType.ATTORNEY) {
      this.subs.push(
        this.userService.getUserSuggestions(false).subscribe((users) => {
          this.userSuggestions = users;
        })
      );
    }

    this.selectedAttorneyId$.next(attorneyId);
    this.selectedClient$.next(this.selectedClient);
    this.isInitialized = true;
    this.isInitialized$.next(true);
  }

  public clearForm() {
    this.splitForm.reset();
    this.removeForms();
    this.propertyPlaintiffs = [];
    this.propertyPrimaryPlaintiff = null;
    this.isInitialized = false;
    this.isInitialized$.next(false);
  }

  public getSectionDescription(section: IntakeFormSections): string {
    switch (section) {
      case IntakeFormSections.USER:
        return (
          this.intakeForm?.sections[IntakeFormSections.USER].description ??
          'Specify the contact information for the user who will be filing the eviction.'
        );
      case IntakeFormSections.ORGANIZATION:
        return (
          this.intakeForm?.sections[IntakeFormSections.ORGANIZATION].description ??
          'Specify the business information for the client who will be filing the eviction.'
        );
      case IntakeFormSections.PLAINTIFF:
        return this.intakeForm?.sections[IntakeFormSections.PLAINTIFF].description ?? '';
      case IntakeFormSections.PROPERTY:
        return this.intakeForm?.sections[IntakeFormSections.PROPERTY].description ?? '';
      case IntakeFormSections.TENANT:
        return this.intakeForm?.sections[IntakeFormSections.TENANT].description ?? '';
      case IntakeFormSections.CLAIMS:
        return this.intakeForm?.sections[IntakeFormSections.CLAIMS].description ?? '';
      case IntakeFormSections.LEASE_TERMS:
        return (
          this.intakeForm?.sections[IntakeFormSections.LEASE_TERMS].description ??
          'Specify the terms of the lease and other details.'
        );
      case IntakeFormSections.INTERNAL:
        return this.intakeForm?.sections[IntakeFormSections.INTERNAL].description ?? '';
      case IntakeFormSections.OTHER:
        return this.intakeForm?.sections[IntakeFormSections.OTHER].description ?? '';
      case IntakeFormSections.DOCUMENTS:
        return this.intakeForm?.sections[IntakeFormSections.DOCUMENTS].description ?? '';
      default:
        return '';
    }
  }

  public getSectionLocalization(section: IntakeFormSections): { [key: string]: string | undefined } {
    switch (section) {
      case IntakeFormSections.USER:
        return this.intakeForm?.sections[IntakeFormSections.USER].localization ?? {};
      case IntakeFormSections.ORGANIZATION:
        return this.intakeForm?.sections[IntakeFormSections.ORGANIZATION].localization ?? {};
      case IntakeFormSections.PLAINTIFF:
        return this.intakeForm?.sections[IntakeFormSections.PLAINTIFF].localization ?? {};
      case IntakeFormSections.TENANT:
        return this.intakeForm?.sections[IntakeFormSections.TENANT].localization ?? {};
      case IntakeFormSections.PROPERTY:
        return this.intakeForm?.sections[IntakeFormSections.PROPERTY].localization ?? {};
      case IntakeFormSections.CLAIMS:
        return this.intakeForm?.sections[IntakeFormSections.CLAIMS].localization ?? {};
      case IntakeFormSections.LEASE_TERMS:
        return this.intakeForm?.sections[IntakeFormSections.LEASE_TERMS].localization ?? {};
      default:
        return {};
    }
  }

  public updateWorkflow(workflows: Workflow[]) {
    if (workflows?.length) {
      // The org's default when it is actually in this list, otherwise the first workflow — the same
      // priority `workflow-form.component` already applies.
      //
      // `find` was trusted to succeed, and the undefined it returns overwrote the fallback, so `.id` on the
      // next line threw and took the rest of the method with it: `workflows` was never assigned and
      // `workflows$` never emitted, leaving New Eviction with an empty Workflow section and nothing but a
      // console error to explain it.
      //
      // The id and the list go out of step easily. `defaultWorkflowId` is copied from the logged-in org in
      // `initialize`, and that org only refreshes at login, while the list is re-fetched on every visit. So
      // any workflow that stops being offered — deleted, or disabled and filtered out by the intake page —
      // leaves the id pointing at nothing. Guarding here is right regardless; keeping the org in step when
      // the default changes is handled where the change is made.
      const orgDefault = this.defaultWorkflowId
        ? workflows.find((w) => w.id === this.defaultWorkflowId)
        : undefined;
      this.selectedWorkflow = orgDefault ?? workflows[0];
      this.workflowDetails.patchValue({ workflow_id: this.selectedWorkflow.id });
      this.workflows = workflows;
      this.workflows$.next(workflows);
    }
  }

  public updateSelectedWorkflow(workflow: Workflow) {
    if (workflow) {
      this.selectedWorkflow = workflow;
      this.workflowDetails.patchValue({ workflow_id: workflow.id });
    }
  }

  public updateClient(client: Organization) {
    this.workflowDetails.patchValue({ client_details: client });
    this.selectedClient = client;
    this.selectedClient$.next(client);
  }

  public updateAttorneyUser(attorneyUserId: string) {
    this.workflowDetails.patchValue({ selected_attorney_user_id: attorneyUserId });
  }

  public updateAttorney(attorneyId: string) {
    this.workflowDetails.patchValue({ attorney_id: attorneyId });
    this.attorneyId = attorneyId;
    this.selectedAttorneyId$.next(attorneyId);
  }

  public generateEvictionDetails(): Eviction | undefined {
    if (
      !this.tenantDetails ||
      !this.propertyDetails ||
      !this.claimDetails ||
      !this.leaseTermsDetails ||
      !this.documents
    ) {
      return undefined;
    }
    const tenantDetails = this.tenantDetails.value;
    const propertyDetails = this.propertyDetails.value;
    const claimDetails = this.claimDetails.value;
    const leaseTermsDetails = this.leaseTermsDetails.value;
    const internalDetails = this.internalDetails?.value;

    let internalFields = {};
    if (this.setupInternalFields) {
      internalFields = internalDetails?.custom_fields ?? {};
    }

    // copy custom fields into eviction details
    const evictionDetails = {
      ...tenantDetails,
      ...propertyDetails,
      ...leaseTermsDetails,
      ...claimDetails,
      custom_fields: {
        ...(tenantDetails.custom_fields ?? {}),
        ...(propertyDetails.custom_fields ?? {}),
        ...(leaseTermsDetails.custom_fields ?? {}),
        ...(claimDetails.custom_fields ?? {}),
        ...internalFields
      }
    };

    this.evictionDetails = evictionDetails;
    return evictionDetails;
  }

  public removeForms() {
    this.splitForm.removeControl('tenant_details');
    this.splitForm.removeControl('property_details');
    this.splitForm.removeControl('claim_details');
    this.splitForm.removeControl('lease_terms_details');
    this.splitForm.removeControl('documents');
    this.splitForm.removeControl('existing_documents');
    this.plaintiffHistory = [];
    this.evictionDetails = undefined;
  }

  public async setupForms(cloneCase: Eviction | null = null) {
    // fully expect attorney id to be specified by the time this method is called
    const attorneyId = this.workflowDetails.get('attorney_id')?.value;
    if (attorneyId) {
      // Awaited (not just subscribed) so callers — e.g. CreateEvictionComponent's `loading` flag — don't
      // clear their own loading state before intakeForm/splitForm controls are actually populated. This
      // used to fire-and-forget the subscription, letting `initialize()`/`setupForms()` resolve before the
      // fetch completed; the stepper would then render with a step whose content depends on `intakeForm`
      // being set, racing the fetch on live/slower networks (observed as a 30s+ hang in e2e tests waiting
      // for `ee-eviction-property-details` to attach).
      const intakeForm = await firstValueFrom(
        this.intakeFormService.findByTypeAndOrganizationId(IntakeFormType.EVICTION, attorneyId)
      );
      this.intakeForm = intakeForm;

      // setup form. The trailing `true` opts these six into applying each field's authored default:
      // this is the only caller building a form for a case that does not exist yet. The edit-* dialogs
      // construct the same sections over a saved case and must NOT (see initialValueFor).
      this.splitForm.addControl(
        'tenant_details',
        GenerateEvictionTenantForm(this.fb, intakeForm.options, this.tenantsSection, cloneCase, true)
      );

      this.splitForm.addControl(
        'property_details',
        GenerateEvictionPropertyForm(this.fb, intakeForm.options, this.propertySection, cloneCase, true)
      );

      this.splitForm.addControl(
        'claim_details',
        GenerateEvictionClaimsForm(this.fb, intakeForm.options, this.claimsSection, cloneCase, true)
      );

      this.splitForm.addControl(
        'lease_terms_details',
        GenerateEvictionLeaseTermsForm(this.fb, intakeForm.options, this.leaseTermsSection, cloneCase, true)
      );

      if (this.setupInternalFields) {
        this.splitForm.addControl(
          'internal_details',
          GenerateInternalFieldsForm(this.fb, intakeForm.options, this.internalSection, cloneCase, true)
        );
      }

      if (this.setupOtherFields) {
        this.splitForm.addControl(
          'other_details',
          GenerateOtherFieldsForm(this.fb, intakeForm.options, this.otherSection, true)
        );
      }

      this.splitForm.addControl('documents', GenerateEvictionDocumentForm(this.fb, intakeForm, cloneCase));

      if (cloneCase) {
        this.splitForm.addControl(
          'existing_documents',
          GenerateExistingAttachmentsForm(this.fb, intakeForm, cloneCase)
        );
      }

      this.propertyDetails?.patchValue({
        company_name: cloneCase ? cloneCase.company_name : this.selectedClient?.company_name
      });
    }

    if (this.propertyPlaintiffs.length) {
      // Property-linked PM mode: the property's owners ARE the plaintiff options — they replace the
      // built-in previously-used-plaintiffs history entirely, and the primary owner is preselected
      // (a sole owner simply fills the field; the autocomplete stays disabled below 2 options).
      this.plaintiffHistory = [...this.propertyPlaintiffs];
      if (this.propertyPrimaryPlaintiff) {
        this.propertyDetails?.patchValue({company_name: this.propertyPrimaryPlaintiff});
      }
    } else {
      // find historical list of plaintiffs
      this.plaintiffHistory = await this.clientService.getPlaintiffHistory(
        this.workflowDetails.get('client_details').value?.id,
        this.workflowDetails.get('attorney_id').value,
        this.workflowDetails.get('client_details').value?.company_name,
        this.isPublicIntakeForm
      );
    }
  }

  /**
   * Property-linked PM mode: seed the plaintiff options from the case's property owners. `owners`
   * replaces the previously-used-plaintiffs autocomplete list; `primary` (defaults to the first
   * owner) is preselected into the Plaintiff Name field when the property sections build. Call
   * before [setupForms] runs (the slide-in sets it before `initialize`).
   */
  setPropertyPlaintiffs(owners: string[], primary?: string | null): void {
    this.propertyPlaintiffs = (owners ?? []).map(o => o?.trim()).filter(o => !!o);
    this.propertyPrimaryPlaintiff = primary?.trim() || this.propertyPlaintiffs[0] || null;
  }

  public removeExistingDocument(index: number) {
    this.existingDocuments?.removeAt(index);
    this.existingDocuments?.updateValueAndValidity();
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  get workflowDetails(): WorkflowDetailsForm | null {
    return this.splitForm ? (this.splitForm.controls.workflow_details as WorkflowDetailsForm) : null;
  }

  get tenantDetails(): UntypedFormGroup | null {
    return this.splitForm ? (this.splitForm.controls.tenant_details as UntypedFormGroup) : null;
  }

  get tenants(): Tenant[] | null {
    return this.tenantDetails?.controls.tenants.value;
  }

  get propertyDetails(): UntypedFormGroup | null {
    return this.splitForm ? (this.splitForm.controls.property_details as UntypedFormGroup) : null;
  }

  get claimDetails(): UntypedFormGroup | null {
    return this.splitForm ? (this.splitForm.controls.claim_details as UntypedFormGroup) : null;
  }

  get leaseTermsDetails(): UntypedFormGroup | null {
    return this.splitForm ? (this.splitForm.controls.lease_terms_details as UntypedFormGroup) : null;
  }

  get internalDetails(): UntypedFormGroup | null {
    return this.splitForm ? (this.splitForm.controls.internal_details as UntypedFormGroup) : null;
  }

  get otherDetails(): UntypedFormGroup | null {
    return this.splitForm ? (this.splitForm.controls.other_details as UntypedFormGroup) : null;
  }

  get documents(): UntypedFormArray | null {
    return this.splitForm ? (this.splitForm.controls.documents as UntypedFormArray) : null;
  }

  get existingDocuments(): UntypedFormArray | null {
    return this.splitForm ? (this.splitForm.controls.existing_documents as UntypedFormArray) : null;
  }

  get options(): Map<string, IntakeOption> | undefined {
    return this.intakeForm?.options;
  }

  get tenantsSection(): IntakeFormSection | undefined {
    return this.intakeForm?.sections[IntakeFormSections.TENANT] ?? undefined;
  }

  get propertySection(): IntakeFormSection | undefined {
    return this.intakeForm?.sections[IntakeFormSections.PROPERTY] ?? undefined;
  }

  get claimsSection(): IntakeFormSection | undefined {
    return this.intakeForm?.sections[IntakeFormSections.CLAIMS] ?? undefined;
  }

  get leaseTermsSection(): IntakeFormSection | undefined {
    return this.intakeForm?.sections[IntakeFormSections.LEASE_TERMS] ?? undefined;
  }

  get internalSection(): IntakeFormSection | undefined {
    return this.intakeForm?.sections[IntakeFormSections.INTERNAL] ?? undefined;
  }

  get otherSection(): IntakeFormSection | undefined {
    return this.intakeForm?.sections[IntakeFormSections.OTHER] ?? undefined;
  }

  get documentSection(): IntakeFormSection | undefined {
    return this.intakeForm?.sections[IntakeFormSections.DOCUMENTS] ?? undefined;
  }

  get intakeStep(): WorkflowStep | undefined {
    return this.selectedWorkflow?.steps.find((s) => s.step === 1);
  }

  get evictionReason(): string {
    return this.tenantDetails?.get(IntakeFormFields.EVICTION_REASON)?.value;
  }

  get setupInternalFields(): boolean {
    return this.loggedInOrgType === AccountType.ATTORNEY && this.internalSection?.fields.length > 0;
  }

  get setupOtherFields(): boolean {
    return this.otherSection?.fields.length > 0;
  }
}
